import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { LogoMark } from "./Logo";
import { MaskLine } from "./Reveal";
import { PRICE, PRODUCT_NAME, scrollToId, EASE } from "@/lib/brand";

const HeroCard = ({ rx, ry }) => (
  <motion.button
    data-testid="hero-product-card"
    onClick={() => scrollToId("shop")}
    style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }}
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1.2, ease: EASE, delay: 1.5 }}
    className="group w-full max-w-[290px] text-left rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-xl p-3 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
  >
    <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-black">
      <img src="/images/p-black.jpg" alt="Black MenTellHealth hoodie" className="h-full w-full object-cover scale-110 transition-transform duration-700 group-hover:scale-125" />
      <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-1 font-mono text-[10px] uppercase tracking-widest">Drop 01</span>
    </div>
    <div className="flex items-end justify-between px-1 pt-3">
      <div>
        <p className="font-display font-bold text-sm">{PRODUCT_NAME}</p>
        <p className="font-mono text-[11px] text-slate-300 uppercase tracking-widest">4 colours · XS–XXXL</p>
      </div>
      <p className="font-display text-xl font-extrabold text-[var(--mth-blue)]">£{PRICE}</p>
    </div>
  </motion.button>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const ry = useTransform(sx, [-0.5, 0.5], [-12, 12]);
  const rx = useTransform(sy, [-0.5, 0.5], [10, -10]);
  const bgX = useTransform(sx, [-0.5, 0.5], ["1.2%", "-1.2%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[#07090D]">
      <motion.div style={{ y: bgY, x: bgX }} className="absolute inset-[-4%]">
        <motion.img
          src="/images/hero.jpg"
          alt="Man in a MenTellHealth hoodie sitting above a mountain lake at dusk"
          initial={{ scale: 1.25, opacity: 0 }}
          animate={{ scale: 1.08, opacity: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="h-full w-full object-cover object-[60%_center]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#07090D] via-[#07090D]/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#07090D] via-transparent to-[#07090D]/40" />

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 pt-40 sm:pt-44 pb-16 min-h-[100svh] flex flex-col justify-end">
        <div className="flex items-center gap-3 mb-8">
          <LogoMark draw className="h-9 w-auto" />
          <motion.p initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2, duration: 0.8 }} className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.28em] text-slate-200">
            Real men <span className="text-[var(--mth-blue)]">•</span> Real conversations <span className="text-[var(--mth-blue)]">•</span> Brighter tomorrows
          </motion.p>
        </div>
        <h1 data-testid="hero-headline" className="font-display font-black uppercase leading-[0.86] tracking-[-0.03em] text-[15vw] sm:text-[12vw] lg:text-[9.5vw] xl:text-[9rem]">
          <MaskLine delay={0.35}>More than</MaskLine>
          <MaskLine delay={0.5}>
            a <span className="text-outline">hoodie</span><span className="text-[var(--mth-blue)]">.</span>
          </MaskLine>
        </h1>
        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_auto] lg:items-end">
          <div className="max-w-xl">
            <MaskLine delay={0.8}>
              <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
                Every MenTellHealth hoodie carries a message: <span className="text-white font-semibold">talk, connect, keep going.</span> Wear it, start the conversation, and help more men find their voice.
              </p>
            </MaskLine>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: EASE }} className="mt-8 flex flex-wrap items-center gap-4">
              <button data-testid="hero-cta-shop" onClick={() => scrollToId("shop")} className="glow-btn group">
                Get yours today <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button data-testid="hero-cta-story" onClick={() => scrollToId("story")} className="font-mono text-xs uppercase tracking-[0.22em] text-slate-200 hover:text-white link-underline">
                Our story
              </button>
            </motion.div>
          </div>
          <HeroCard rx={rx} ry={ry} />
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-slate-300 [writing-mode:vertical-rl]">
          Scroll <ArrowDown className="h-3 w-3 animate-bounce" />
        </motion.div>
      </motion.div>
    </section>
  );
};
